import React, { useState } from "react";
import { StickyNote, Bell, FileText, KeyRound, Search, ScanLine, Image as ImageIcon, Copy, Eye, ChevronRight, RotateCw, BookText, TrendingUp, ShieldCheck, HeartPulse, Plane } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { useNavigate } from "react-router-dom";

function Base({ icon: Icon, title, badge, badgeTone = "default", children, testId }) {
  const toneMap = {
    default: "bg-secondary text-secondary-foreground",
    saved: "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]",
    locked: "bg-muted text-muted-foreground",
    scheduled: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]",
    found: "bg-[hsl(var(--info))]/12 text-[hsl(var(--info))]",
  };
  return (
    <Card className="rounded-2xl border border-border bg-card shadow-[0_10px_30px_rgba(15,23,42,0.06)] impo-in overflow-hidden" data-testid={testId}>
      <div className="flex items-start justify-between gap-3 p-4">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] flex items-center justify-center">
            <Icon className="h-5 w-5" />
          </div>
          <div className="font-display font-semibold text-sm">{title}</div>
        </div>
        {badge && <Badge className={`text-[10px] rounded-full ${toneMap[badgeTone] || toneMap.default}`}>{badge}</Badge>}
      </div>
      <div className="px-4 pb-4">{children}</div>
    </Card>
  );
}

export default function ActionCard({ action }) {
  const nav = useNavigate();
  const [revealed, setRevealed] = useState({});

  if (!action) return null;
  const t = action.type;
  const d = action.data || {};

  if (t === "note_saved") {
    return (
      <Base icon={StickyNote} title="Note saved" badge="Saved" badgeTone="saved" testId="chat-action-card-note-saved">
        {d.title && <div className="font-medium text-sm mb-1">{d.title}</div>}
        <p className="text-sm text-muted-foreground line-clamp-3">{d.content}</p>
        <button onClick={() => nav("/app/notes")} className="mt-3 text-xs font-medium text-primary inline-flex items-center gap-1" data-testid="chat-action-open-notes">
          Open notes <ChevronRight className="h-3 w-3" />
        </button>
      </Base>
    );
  }

  if (t === "reminder_set") {
    const dt = d.due_at ? new Date(d.due_at) : null;
    return (
      <Base icon={Bell} title="Reminder scheduled" badge={d.recurrence && d.recurrence !== "none" ? d.recurrence : "Scheduled"} badgeTone="scheduled" testId="chat-action-card-reminder-set">
        <div className="text-sm font-medium">{d.text}</div>
        <div className="text-xs text-muted-foreground mt-1">
          {dt ? dt.toLocaleString() : (d.recurrence && d.recurrence !== "none" ? `Recurring • ${d.recurrence}` : "No time set")}
        </div>
        <button onClick={() => nav("/app/reminders")} className="mt-3 text-xs font-medium text-primary inline-flex items-center gap-1" data-testid="chat-action-open-reminders">
          View reminders <ChevronRight className="h-3 w-3" />
        </button>
      </Base>
    );
  }

  if (t === "password_saved") {
    return (
      <Base icon={KeyRound} title="Password saved" badge="Encrypted" badgeTone="locked" testId="chat-action-card-password-saved">
        <div className="text-sm font-medium">{d.label}</div>
        {d.username && <div className="text-xs text-muted-foreground">{d.username}</div>}
        <div className="font-mono text-xs mt-2 tracking-widest">••••••••••</div>
        <button onClick={() => nav("/app/passwords")} className="mt-3 text-xs font-medium text-primary inline-flex items-center gap-1" data-testid="chat-action-open-passwords">
          Open vault <ChevronRight className="h-3 w-3" />
        </button>
      </Base>
    );
  }

  if (t === "password_updated") {
    return (
      <Base icon={RotateCw} title="Password updated" badge="Rotated" badgeTone="saved" testId="chat-action-card-password-updated">
        <div className="text-sm font-medium">{d.label}</div>
        {d.username && <div className="text-xs text-muted-foreground">{d.username}</div>}
        <div className="text-xs text-muted-foreground mt-2">Previous versions in history: <b>{d.history_count || 0}</b></div>
        <button onClick={() => nav("/app/passwords")} className="mt-3 text-xs font-medium text-primary inline-flex items-center gap-1" data-testid="chat-action-open-passwords">
          Open vault <ChevronRight className="h-3 w-3" />
        </button>
      </Base>
    );
  }

  if (t === "journal_saved") {
    return (
      <Base icon={BookText} title="Journal entry saved" badge={d.mood || "polished"} badgeTone="scheduled" testId="chat-action-card-journal-saved">
        {d.summary && <div className="text-xs italic text-muted-foreground mb-1">{d.summary}</div>}
        <p className="text-sm line-clamp-4 whitespace-pre-wrap">{d.preview}</p>
        <button onClick={() => nav("/app/journal")} className="mt-3 text-xs font-medium text-primary inline-flex items-center gap-1" data-testid="chat-action-open-journal">
          Open journal <ChevronRight className="h-3 w-3" />
        </button>
      </Base>
    );
  }

  if (t === "vertical_saved") {
    const VERTICAL_META = {
      investment: { icon: TrendingUp,  label: "Investment saved", to: "/app/investments", tone: "saved" },
      insurance:  { icon: ShieldCheck, label: "Insurance saved",  to: "/app/insurance",   tone: "saved" },
      medical:    { icon: HeartPulse,  label: "Medical record saved", to: "/app/medical", tone: "saved" },
      travel:     { icon: Plane,       label: "Trip saved",       to: "/app/travel",      tone: "saved" },
    };
    const meta = VERTICAL_META[d.vertical] || VERTICAL_META.investment;
    return (
      <Base icon={meta.icon} title={meta.label} badge="Saved" badgeTone={meta.tone} testId={`chat-action-card-${d.vertical}-saved`}>
        {d.title && <div className="font-medium text-sm mb-1">{d.title}</div>}
        {d.subtitle && <div className="text-xs text-muted-foreground line-clamp-2">{d.subtitle}</div>}
        <button
          onClick={() => nav(meta.to)}
          className="mt-3 text-xs font-medium text-primary inline-flex items-center gap-1"
          data-testid={`chat-action-open-${d.vertical}`}
        >
          Open {d.vertical === "travel" ? "trips" : `${d.vertical}s`} <ChevronRight className="h-3 w-3" />
        </button>
      </Base>
    );
  }

  if (t === "request_upload") {
    return (
      <Base icon={d.data?.kind === "photo" ? ImageIcon : ScanLine} title={`Attach ${d.kind || "file"}`} badge="Waiting" badgeTone="default" testId="chat-action-card-request-upload">
        <p className="text-sm text-muted-foreground">Open the scanner to capture or upload a file. I'll auto-file it in your vault.</p>
        <button onClick={() => nav("/app/scanner")} className="mt-3 text-xs font-medium text-primary inline-flex items-center gap-1" data-testid="chat-action-open-scanner">
          Open scanner <ChevronRight className="h-3 w-3" />
        </button>
      </Base>
    );
  }

  if (t === "retrieval_results") {
    const results = d.results || [];
    return (
      <Base icon={Search} title={`Found ${results.length} match${results.length === 1 ? "" : "es"}`} badge="Found" badgeTone="found" testId="chat-action-card-retrieval-result">
        <div className="space-y-2">
          {results.length === 0 && <div className="text-sm text-muted-foreground">Nothing matched that yet.</div>}
          {results.map((r) => <RetrievalRow key={r.id} r={r} revealed={revealed} setRevealed={setRevealed} />)}
        </div>
      </Base>
    );
  }

  return null;
}

function RetrievalRow({ r, revealed, setRevealed }) {
  const nav = useNavigate();
  const typeLabel = {
    note: "Note", document: "Document", photo: "Photo", reminder: "Reminder", password: "Password",
  }[r.type] || r.type;

  const reveal = async () => {
    try {
      const { data } = await api.get(`/passwords/${r.id}/reveal`);
      setRevealed((s) => ({ ...s, [r.id]: data.password }));
      toast.success("Password revealed");
    } catch { toast.error("Could not reveal password"); }
  };

  const copy = async (val) => {
    try { await navigator.clipboard.writeText(val); toast.success("Copied to clipboard"); } catch { toast.error("Copy failed"); }
  };

  const openTarget = () => {
    if (r.type === "note") nav("/app/notes");
    else if (r.type === "document") nav("/app/documents");
    else if (r.type === "photo") nav("/app/photos");
    else if (r.type === "reminder") nav("/app/reminders");
    else if (r.type === "password") nav("/app/passwords");
  };

  return (
    <div className="flex items-start gap-3 p-2 rounded-xl border border-border bg-background" data-testid="chat-retrieval-row">
      {r.file_url ? (
        r.type === "photo" || r.type === "document" ? (
          <img src={fileUrl(r.file_url)} alt="" className="h-10 w-10 rounded-lg object-cover border border-border" />
        ) : null
      ) : (
        <div className="h-10 w-10 rounded-lg bg-accent text-accent-foreground flex items-center justify-center text-xs">{typeLabel[0]}</div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{typeLabel}</span>
        </div>
        <div className="text-sm font-medium truncate">{r.title || "Untitled"}</div>
        <div className="text-xs text-muted-foreground line-clamp-2">{r.snippet}</div>
        {r.type === "password" && (
          <div className="mt-1">
            {revealed[r.id] ? (
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs">{revealed[r.id]}</span>
                <button onClick={() => copy(revealed[r.id])} className="text-xs text-primary inline-flex items-center gap-1">
                  <Copy className="h-3 w-3" /> Copy
                </button>
              </div>
            ) : (
              <button onClick={reveal} className="text-xs text-primary inline-flex items-center gap-1" data-testid="chat-retrieval-reveal-password">
                <Eye className="h-3 w-3" /> Reveal
              </button>
            )}
          </div>
        )}
      </div>
      <button onClick={openTarget} className="h-8 w-8 rounded-full hover:bg-muted flex items-center justify-center" aria-label="Open">
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}
